/**
 * Caja "Historial del dispositivo" en la ficha de reparación.
 * Lista las reparaciones previas que comparten el mismo número de serie / IMEI.
 * Extraído de `RepairForm.jsx`.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import api from "@/lib/api";
import Box from "@/components/repairs/Box";

export default function BoxSerialHistory({ serial, excludeId }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Iter 109 — Nº de serie normalizado a MAYÚSCULAS + mínimo 4 chars para
    // relacionar reparaciones anteriores (con menos de 4 no consultamos).
    const s = (serial || "").trim().toUpperCase();
    if (s.length < 4) {
      setList([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/repairs/by-serial/${encodeURIComponent(s)}`, {
          params: excludeId ? { exclude_id: excludeId } : {},
        });
        setList(r.data);
      } catch {
        setList([]);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [serial, excludeId]);

  return (
    <Box title="Historial del dispositivo" testid="repair-box-history">
      {!serial || (serial || "").trim().length < 4 ? (
        <div className="text-xs text-[#525252]">
          Introduce al menos 4 caracteres del número de serie / IMEI para ver el historial.
        </div>
      ) : loading ? (
        <div className="text-xs text-[#525252]">Buscando...</div>
      ) : list.length === 0 ? (
        <div className="text-xs text-[#525252]" data-testid="repair-history-empty">
          No existen reparaciones anteriores para este número de serie / IMEI.
        </div>
      ) : (
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#525252] mb-2">
            {list.length} reparación{list.length !== 1 ? "es" : ""} anterior{list.length !== 1 ? "es" : ""}
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left font-mono text-[9px] uppercase tracking-widest text-[#525252] border-b border-[#E5E5E0]">
                <th className="py-1.5">Nº rep.</th>
                <th className="py-1.5">Fecha</th>
                <th className="py-1.5">Equipo</th>
                <th className="py-1.5 w-6"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id} className="border-b border-[#E5E5E0] last:border-0 hover:bg-[#F7F7F4]" data-testid={`repair-history-${r.code}`}>
                  <td className="py-1.5 font-mono">{r.code}</td>
                  <td className="py-1.5 font-mono text-[10px]">
                    {new Date(r.entry_date || r.created_at).toLocaleDateString("es")}
                  </td>
                  <td className="py-1.5 text-[#525252] truncate max-w-[12ch]">
                    {r.device_type} {r.model}
                  </td>
                  <td className="py-1.5">
                    <Link
                      to={`/repairs/${r.id}`}
                      className="text-[#6E8B3D] hover:text-[#556C2E]"
                      title="Ver reparación"
                    >
                      <ExternalLink size={11} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Box>
  );
}
